import React from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import DownChevron from '@atlaskit/icon/glyph/hipchat/chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { CmdbNumericStatusCategory } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/status.tsx';

export type Status = {
	id: string;
	name: string;
	category: CmdbNumericStatusCategory | null;
};

export const StatusLozenge = ({
	isEditable,
	status,
	ellipsis,
}: {
	isEditable: boolean;
	status: Status;
	ellipsis?: boolean;
}) => {
	const { name, category } = status;
	return (
		<LozengeOverrides
			// @ts-expect-error - TS2322: Type 'null' is not assignable to type '0 | 1 | 2 | null'.
			category={category}
			isEditable={isEditable}
			data-testid="servicedesk-insight-attribute-views.ui.status.status-lozenge"
		>
			<Lozenge>
				<LozengeContent>
					{isEditable || ellipsis ? <EllipsisOverflow>{name}</EllipsisOverflow> : name}
					{isEditable && <DownChevron size="small" label="" />}
				</LozengeContent>
			</Lozenge>
		</LozengeOverrides>
	);
};

const statusCategory = {
	'0': 'inactive',
	'1': 'active',
	'2': 'pending',
	null: 'none',
} as const;

const statusBackground = {
	inactive: token('color.background.danger', colors.R50),
	active: token('color.background.success', colors.G50),
	pending: token('color.background.warning', colors.Y75),
	none: token('color.background.neutral', colors.N40),
} as const;

const bgHoverColor = {
	inactive: token('color.background.danger.hovered', colors.R75),
	active: token('color.background.success.hovered', colors.G75),
	pending: token('color.background.warning.hovered', colors.Y100),
	none: token('color.background.neutral.hovered', colors.N50),
} as const;

const textColor = {
	inactive: token('color.text.danger', colors.R500),
	active: token('color.text.success', colors.G500),
	pending: token('color.text', colors.N800),
	none: token('color.text.subtle', colors.N500),
} as const;

type LozengeOverridesType = {
	isEditable?: boolean;
	category: keyof typeof statusCategory;
};

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeOverrides = styled.div<LozengeOverridesType>`
	display: flex;
	align-items: center;

	> span {
		padding: ${(props) => (props.isEditable ? '4px 6px' : '2px')};
		vertical-align: text-bottom;
		color: ${(props) => textColor[statusCategory[props.category]]};
		background-color: ${(props) => statusBackground[statusCategory[props.category]]};
		${(props) =>
			props.isEditable
				? css({
						'&:hover': {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							backgroundColor: `${bgHoverColor[statusCategory[props.category]]}`,
						},
					})
				: ''};
	}

	> span > span {
		text-overflow: clip;
		white-space: normal;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContent = styled2.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EllipsisOverflow = styled2.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
